import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getJobs } from '../data/careersData';
import { sendEmail } from '../services/emailService';

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [applyForm, setApplyForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [applyJob, setApplyJob] = useState(null);
  const [applySubmitted, setApplySubmitted] = useState(false);

  useEffect(() => {
    setJobs(getJobs());
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const onStorage = () => setJobs(getJobs());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${m}/${d}/${y}`;
  };

  const today = new Date().toISOString().split('T')[0];

  const filtered = jobs.filter(j =>
    j.title.toLowerCase().includes(search.toLowerCase()) ||
    j.location.toLowerCase().includes(search.toLowerCase())
  );

  const active = filtered.filter(j => j.published && j.closingDate >= today);
  const expired = filtered.filter(j => j.published && j.closingDate < today);

  const handleApply = (e, job) => {
    e.preventDefault();
    sendEmail({
      from_name: applyForm.name,
      from_email: applyForm.email,
      phone: applyForm.phone,
      subject: `Job Application: ${job.title} - EGB Systems`,
      message: `Job Title: ${job.title}\nLocation: ${job.location}\n\nCover Letter / Message:\n${applyForm.message}`,
    }).then(() => {
      setApplySubmitted(true);
      setApplyForm({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => { setApplySubmitted(false); setApplyJob(null); }, 3000);
    }).catch(() => {
      alert('Failed to submit. Please email us at support@egbsystems.com');
    });
  };

  return (
    <>
      <section className="py-5 text-white text-center" style={{ background: 'linear-gradient(135deg, #002868, #1a56db)' }}>
        <div className="container py-4">
          <h1 className="fw-bold">Careers at EGB Systems</h1>
          <p className="lead" style={{ color: '#cbd5e1' }}>Join our team and build the future of technology</p>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container py-4">

          {/* Search */}
          <div className="row justify-content-center mb-5">
            <div className="col-12 col-md-6">
              <input
                type="text"
                className="form-control form-control-lg shadow-sm"
                placeholder="Search by job title or location..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Active Jobs */}
          <h4 className="fw-bold mb-4">Current Openings <span className="badge bg-primary ms-2">{active.length}</span></h4>
          {active.length === 0 && <p className="text-muted">No current openings found.</p>}
          <div className="row g-4 mb-5">
            {active.map(job => (
              <div key={job.id} className="col-12">
                <div className="card border-0 shadow-sm p-4">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                    <div>
                      <h5 className="fw-bold mb-1">{job.title}</h5>
                      <div className="d-flex flex-wrap gap-3 mb-2">
                        <span className="small text-muted">📍 {job.location}</span>
                        <span className="small text-muted">📅 Posted: {formatDate(job.postedDate)}</span>
                        <span className="small text-danger fw-semibold">⏰ Closes: {formatDate(job.closingDate)}</span>
                      </div>
                      <p className="text-muted small mb-0">{job.description.substring(0, 150)}...</p>
                    </div>
                    <button
                      className="btn btn-primary flex-shrink-0"
                      onClick={() => setSelected(selected?.id === job.id ? null : job)}
                    >
                      {selected?.id === job.id ? 'Hide Details' : 'View Details'}
                    </button>
                  </div>

                  {selected?.id === job.id && (
                    <div className="mt-4 pt-4 border-top">
                      <h6 className="fw-bold mb-2">Job Description</h6>
                      <p className="text-muted" style={{ lineHeight: 1.8 }}>{job.description}</p>

                      {applyJob?.id === job.id ? (
                        applySubmitted ? (
                          <div className="alert alert-success mt-3">✅ Application submitted! We will contact you soon.</div>
                        ) : (
                          <form onSubmit={(e) => handleApply(e, job)} className="mt-3 p-4 bg-light rounded-3">
                            <h6 className="fw-bold mb-3">Apply for {job.title}</h6>
                            <div className="row g-3">
                              <div className="col-12 col-md-6">
                                <input type="text" className="form-control" placeholder="Your Name *" value={applyForm.name} onChange={e => setApplyForm({ ...applyForm, name: e.target.value })} required />
                              </div>
                              <div className="col-12 col-md-6">
                                <input type="email" className="form-control" placeholder="Your Email *" value={applyForm.email} onChange={e => setApplyForm({ ...applyForm, email: e.target.value })} required />
                              </div>
                              <div className="col-12">
                                <input type="tel" className="form-control" placeholder="Phone Number" value={applyForm.phone} onChange={e => setApplyForm({ ...applyForm, phone: e.target.value })} />
                              </div>
                              <div className="col-12">
                                <textarea className="form-control" rows={3} placeholder="Cover Letter / Message *" value={applyForm.message} onChange={e => setApplyForm({ ...applyForm, message: e.target.value })} required></textarea>
                              </div>
                              <div className="col-12 d-flex gap-2">
                                <button type="submit" className="btn btn-warning fw-bold">Submit Application →</button>
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setApplyJob(null)}>Cancel</button>
                              </div>
                            </div>
                          </form>
                        )
                      ) : (
                        <button className="btn btn-warning fw-bold mt-2" onClick={() => setApplyJob(job)}>Apply Now →</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Expired Jobs */}
          {expired.length > 0 && (
            <>
              <h4 className="fw-bold mb-4 text-muted">Expired Openings <span className="badge bg-secondary ms-2">{expired.length}</span></h4>
              <div className="row g-4">
                {expired.map(job => (
                  <div key={job.id} className="col-12">
                    <div className="card border-0 shadow-sm p-4 opacity-75">
                      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                        <div>
                          <h5 className="fw-bold mb-1 text-muted">{job.title}</h5>
                          <div className="d-flex flex-wrap gap-3">
                            <span className="small text-muted">📍 {job.location}</span>
                            <span className="small text-muted">📅 Posted: {formatDate(job.postedDate)}</span>
                            <span className="small text-muted">⏰ Closed: {formatDate(job.closingDate)}</span>
                          </div>
                        </div>
                        <span className="badge bg-secondary">Closed</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="py-5 text-white text-center" style={{ background: 'linear-gradient(135deg, #1a56db, #0ea5e9)' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3">Don't See a Suitable Role?</h2>
          <p className="lead mb-4">Send us your resume and we'll keep you in mind for future openings.</p>
          <Link to="/contact" className="btn btn-warning btn-lg fw-bold px-5">Send Your Resume →</Link>
        </div>
      </section>
    </>
  );
};

export default Careers;
